import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_KEY = 'mingo_global_scroll_v1';

function getRouteKey(location) {
  return `${location.pathname}${location.search || ''}`;
}

function isAdminPath(pathname) {
  return String(pathname || '').startsWith('/admin-mingo-2024');
}

function readStore() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStore(next) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore storage quota issues
  }
}

function getPrimaryScrollContainer() {
  return document.querySelector('.mingo-page__scroll');
}

function readCurrentScroll() {
  const container = getPrimaryScrollContainer();
  return {
    windowY: window.scrollY || 0,
    containerY: container ? container.scrollTop || 0 : 0,
  };
}

function restoreScroll(position) {
  const target = position || { windowY: 0, containerY: 0 };
  window.requestAnimationFrame(() => {
    window.scrollTo(0, Number(target.windowY) || 0);
    const container = getPrimaryScrollContainer();
    if (container) container.scrollTop = Number(target.containerY) || 0;
  });
}

export default function GlobalScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const routeKeyRef = useRef(getRouteKey(location));

  useEffect(() => {
    const prevRouteKey = routeKeyRef.current;
    const nextRouteKey = getRouteKey(location);

    // Save scroll position for route we are leaving.
    if (prevRouteKey) {
      const store = readStore();
      store[prevRouteKey] = readCurrentScroll();
      writeStore(store);
    }

    const store = readStore();
    const prevPath = String(prevRouteKey || '').split('?')[0];
    const adminTabSwitch = isAdminPath(location.pathname) && isAdminPath(prevPath);

    // Admin: keep fixed header stable — do not force window scroll on tab hops.
    if (adminTabSwitch && navigationType !== 'POP') {
      routeKeyRef.current = nextRouteKey;
      return;
    }

    // POP(back/forward) restores previous scroll, others start at top.
    if (navigationType === 'POP' && store[nextRouteKey]) restoreScroll(store[nextRouteKey]);
    else restoreScroll({ windowY: 0, containerY: 0 });

    routeKeyRef.current = nextRouteKey;
  }, [location, navigationType]);

  return null;
}
